import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import * as fromMembers from '../../reducers/members.reducer';
import { TeamRole } from '@app/core/accessManagement/roles';
import * as fromRoot from '@app/reducers';
import { Role } from '@app/shared/models/role';
import { Team } from '@app/shared/models/team';
import { User } from '@app/shared/models/user';
import { DialogRef } from '@ngneat/dialog';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { Constant } from 'src/constant';
import { MembersActions } from '../../actions';
import { isEmail } from '@app/shared/helpers/validators';

@Component({
  selector: 'app-modal-add-member',
  templateUrl: './modal-add-member.component.html',
  styleUrls: ['./modal-add-member.component.scss']
})
export class ModalAddMemberComponent implements OnInit, OnDestroy {
  attachableRoles$: Observable<Role[]>;
  teams$: Observable<Team[]>;
  groups$: Observable<any[]>;
  memberCreated$: Observable<User>;
  memberCreationPending$: Observable<boolean>;
  memberCreationError$: Observable<any>;

  attachableMembers: User[];
  subscriptions: Subscription[] = [];
  isInvited = false;
  memberCreationForm: FormGroup;
  roles: Role[];
  submitted = false;
  selectedTeam: Team;
  authenticationModes = [
    { value: Constant.signinMode.standard, translation: 'PREMIUM_ADMIN_MEMBER_MODAL_ADD_MEMBER_PLACEHOLDER_AUTHENTICATION_MODE_STANDARD' },
    { value: Constant.signinMode.sso, translation: 'PREMIUM_ADMIN_MEMBER_MODAL_ADD_MEMBER_PLACEHOLDER_AUTHENTICATION_MODE_SSO' }
  ];
  selectedAuthenticationMode: any;
  selectedMember: User;

  attachableRoles: Role[];
  teamRoles: Role[];
  teamId: string;
  rolesToAttach: Role[];
  selectedRole: Role;
  selectionRoleEnabled = true;

  attachmentTypes = [
    { type: 'TEAM', description: 'PREMIUM_ADMIN_MEMBER_MODAL_ADD_MEMBER_OPTION_ATTACH_TEAM' },
    { type: 'GROUP', description: 'PREMIUM_ADMIN_MEMBER_MODAL_ADD_MEMBER_OPTION_ATTACH_GROUP' },
  ];

  selectedAttachmentType = this.attachmentTypes[0].type;

  constructor(private store$: Store<fromRoot.State & fromMembers.State>, private fb: FormBuilder, public ref: DialogRef) {
    this.attachableMembers = this.ref.data.attachableMembers;
    this.teamRoles = this.ref.data.teamRoles;
    this.teamId = this.ref.data.teamId;
    this.attachableRoles$ = this.store$.select(fromMembers.getAttributableRoles);
    this.memberCreated$ = this.store$.select(fromMembers.getMemberCreated);
    this.memberCreationPending$ = this.store$.select(fromMembers.getMemberCreationPending);
    this.memberCreationError$ = this.store$.select(fromMembers.getMemberCreationError);
    this.initMemberCreationForm();
  }

  ngOnInit() {
    this.subscriptions.push(
      this.attachableRoles$.subscribe(roles => this.attachableRoles = roles),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => {
      sub.unsubscribe();
    });
  }

  get f() { return this.memberCreationForm.controls; }

  initMemberCreationForm() {
    this.memberCreationForm = new FormGroup({
      member: new FormControl(null, Validators.required),
      lastName: new FormControl(null),
      firstName: new FormControl(null),
      authenticationMode: new FormControl(null),
      invited: new FormControl('false'),
      role: new FormControl(null, Validators.required),
    });
  }

  createMember() {
    this.store$.dispatch(MembersActions.addMember({ ...this.memberCreationForm.value, teamId: this.teamId }));
    this.subscriptions.push(this.memberCreated$.subscribe((created) => {
      if (created) {
        this.store$.dispatch(MembersActions.loadMembers({ teamId: this.teamId }));
        this.closeModal();
      }
    }));
  }

  closeModal() {
    this.submitted = false;
    this.memberCreationForm.reset();
    this.subscriptions.forEach(sub => {
      sub.unsubscribe();
    });
    this.ref.close();
  }

  onSelectMemberChange(member) {
    if (!member) {
      this.memberCreationForm.reset();
    }
  }

  onSubmit() {
    this.submitted = true;
    if (this.memberCreationForm.valid) {
      this.createMember();
    }
  }

  addRole() {
    if (this.attachableRoles?.length) {
      this.rolesToAttach.push(this.selectedRole);
      this.attachableRoles = this.attachableRoles.filter((role: Role) => role.name !== this.selectedRole.name);
    }
    this.selectedRole = null;
  }

  removeSelectedRole(roleToRemove) {
    this.rolesToAttach = this.rolesToAttach.filter(role => role.name !== roleToRemove.name);
    this.attachableRoles = [...this.attachableRoles, roleToRemove.role];
    if (!this.rolesToAttach.length) {
      this.selectionRoleEnabled = true;
    }
    this.selectedRole = null;
  }

  toggleInvite() {
    this.isInvited = !this.isInvited;
    return this.isInvited;
  }

  get countItemsToAttach() {
    return this.rolesToAttach.length;
  }

  addEmail(username: string) {
    const regexpSCIM = new RegExp(Constant.regexpExcludeUserSCIM); // NEED TO BE MOVED
    if (isEmail(username) && regexpSCIM.test(username)) {
      return { username };
    }
  }

  isObjectNull(obj) {
    for (const key in obj) {
      if (obj[key] !== null && obj[key] !== '') {
        return false;
      }
    }
    return true;
  }
}
